const IMAGE_NAME_RE = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
const FILE_NAME_RE =
  /\.(png|jpe?g|gif|webp|bmp|svg|pdf|docx?|xlsx?|pptx?|txt|zip|rar|mp3|mp4|mov|webm|m4a|wav)$/i;

export function looksLikeImageName(value) {
  return IMAGE_NAME_RE.test(String(value || "").trim());
}

export function looksLikeFileName(value) {
  return FILE_NAME_RE.test(String(value || "").trim());
}

function pickUrl(...values) {
  for (const value of values) {
    const text = value != null ? String(value).trim() : "";
    if (text) return text;
  }
  return null;
}

function pickMedia(source) {
  if (!source || typeof source !== "object") {
    return {
      imageUrl: null,
      videoUrl: null,
      audioUrl: null,
      fileUrl: null,
      fileName: null,
      messageType: null,
    };
  }
  return {
    imageUrl: pickUrl(source.imageUrl),
    videoUrl: pickUrl(source.videoUrl),
    audioUrl: pickUrl(source.audioUrl),
    fileUrl: pickUrl(source.fileUrl),
    fileName: pickUrl(source.fileName),
    messageType: pickUrl(source.messageType),
  };
}

function inferImageUrl({ imageUrl, fileUrl, fileName, messageType, body }) {
  if (imageUrl) return imageUrl;
  if (!fileUrl) return null;
  if (messageType === "image" || looksLikeImageName(fileName) || looksLikeImageName(body)) {
    return fileUrl;
  }
  return null;
}

/**
 * Une comentário da tarefa com a mensagem do chat (quando o canal da tarefa existe).
 * Esconde o texto quando ele é só o nome do arquivo (ex.: image.png).
 */
export function normalizeTaskComment(comment, message = null) {
  const fromComment = pickMedia(comment);
  const fromMessage = pickMedia(message);
  const body = String(comment?.body ?? message?.content ?? "").trim();
  const rawFileName = fromComment.fileName || fromMessage.fileName || null;
  const fileName = looksLikeFileName(rawFileName)
    ? rawFileName
    : looksLikeFileName(body)
      ? body
      : null;
  const messageType = fromComment.messageType || fromMessage.messageType || null;
  const fileUrl = fromComment.fileUrl || fromMessage.fileUrl;
  const imageUrl = inferImageUrl({
    imageUrl: fromComment.imageUrl || fromMessage.imageUrl,
    fileUrl,
    fileName,
    messageType,
    body,
  });
  const videoUrl = fromComment.videoUrl || fromMessage.videoUrl;
  const audioUrl = fromComment.audioUrl || fromMessage.audioUrl;
  const resolvedType =
    messageType ||
    (imageUrl ? "image" : videoUrl ? "video" : audioUrl ? "audio" : fileUrl ? "file" : "text");
  const hasMedia = Boolean(imageUrl || videoUrl || audioUrl || fileUrl);
  const isFilenameBody =
    Boolean(body) &&
    (body === "image.png" ||
      (looksLikeFileName(body) && (!fileName || body === fileName)));

  return {
    ...comment,
    id: comment?.id ?? message?.id,
    type: comment?.type || (message?.messageType === "system" ? "log" : undefined),
    action: comment?.action || message?.metadata?.action || undefined,
    body,
    displayBody: isFilenameBody ? "" : body,
    authorId: comment?.authorId ?? message?.authorId ?? null,
    authorName: comment?.authorName || message?.authorName || null,
    authorAvatarUrl: comment?.authorAvatarUrl || message?.authorAvatarUrl || null,
    createdAt: comment?.createdAt || message?.createdAt || null,
    editedAt: comment?.editedAt || message?.editedAt || null,
    imageUrl,
    videoUrl,
    audioUrl,
    fileUrl,
    fileName,
    messageType: resolvedType,
    hasMedia,
  };
}

export function commentFromTeamMessage(message) {
  return normalizeTaskComment(
    {
      id: message?.id,
      body: message?.content,
      authorId: message?.authorId,
      authorName: message?.authorName,
      authorAvatarUrl: message?.authorAvatarUrl,
      createdAt: message?.createdAt,
      editedAt: message?.editedAt,
    },
    message
  );
}

export function mergeTaskCommentsWithMessages(comments = [], messages = []) {
  const messagesById = new Map(
    (messages || [])
      .filter((msg) => msg && msg.id != null)
      .map((msg) => [Number(msg.id), msg])
  );
  const merged = (comments || []).map((comment) =>
    normalizeTaskComment(comment, messagesById.get(Number(comment.id)))
  );
  const seen = new Set(merged.map((comment) => Number(comment.id)));
  const extras = (messages || [])
    .filter((msg) => {
      if (msg == null || msg.id == null || seen.has(Number(msg.id))) return false;
      const authorType = String(msg.authorType || "").toLowerCase();
      const messageType = String(msg.messageType || "").toLowerCase();
      return authorType !== "system" && messageType !== "system";
    })
    .map(commentFromTeamMessage);

  return [...merged, ...extras].sort((a, b) => {
    const ta = new Date(a?.createdAt || 0).getTime();
    const tb = new Date(b?.createdAt || 0).getTime();
    if (ta !== tb) return ta - tb;
    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  });
}
